import React, { Component, Fragment } from 'react';
import { Button, Icon, Pagination, Spin, notification } from 'antd';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import { extensionToType } from '../../utils/helpers';
import './GedCatalog.less';

// The catalog's display name isn't necessarily the real file name (it can be
// missing its extension, or have the wrong one), so the actual MIME type of
// the downloaded blob is a more reliable source of truth when available.
const mimeToType = (mimeType) => {
    if (!mimeType) return null;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType === 'text/html') return 'html';
    return null;
};

class GedFileViewer extends Component {
    state = {
        file: null,
        extension: '',
        numPages: null,
        pageNumber: 1,
        scale: 1.0,
        loading: false,
        isFullscreen: false,
        isPanning: false
    };

    pdfViewerRef = React.createRef();

    panning = false;

    panStart = { x: 0, y: 0, scrollLeft: 0, scrollTop: 0 };

    componentDidMount() {
        this.loadFile();
    }

    componentDidUpdate(prevProps) {
        if (this.props.file && prevProps.file && this.props.file.Id !== prevProps.file.Id) {
            this.loadFile();
            return;
        }

        if (this.props.fileBlob !== prevProps.fileBlob) {
            this.loadFile();
        }
    }

    componentWillUnmount() {
        if (this.state.file) {
            URL.revokeObjectURL(this.state.file);
        }

        this.stopPanning();
    }

    loadFile = () => {
        const { file, fileBlob } = this.props;

        if (!file) {
            this.setState({ file: null, extension: '', pageNumber: 1, scale: 1.0, loading: false });
            return;
        }

        if (this.state.file) {
            URL.revokeObjectURL(this.state.file);
        }

        const extension = file.Name.split('.').pop().toLowerCase();
        const previewUrl = fileBlob ? URL.createObjectURL(fileBlob) : null;

        this.setState({
            file: previewUrl,
            extension,
            pageNumber: 1,
            scale: 1.0,
            loading: false
        });
    };

    onDocumentLoadSuccess = ({ numPages }) => {
        this.setState({ numPages });
    };

    scaleIn = () => {
        this.setState(prevState => ({
            scale: prevState.scale + 0.1
        }));
    };

    scaleOut = () => {
        this.setState(prevState => ({
            scale: Math.max(prevState.scale - 0.1, 0.2)
        }));
    };

    changePage = (page) => {
        this.setState({ pageNumber: page });
    };

    toggleFullscreen = () => {
        this.setState(prevState => ({ isFullscreen: !prevState.isFullscreen }));
    };

    onPanStart = (e) => {
        if (!this.pdfViewerRef.current) return;

        // Only the primary mouse button starts a pan.
        if (e.button !== 0) return;

        e.preventDefault();

        this.panning = true;
        this.panStart = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: this.pdfViewerRef.current.scrollLeft,
            scrollTop: this.pdfViewerRef.current.scrollTop
        };

        this.setState({ isPanning: true });
        document.addEventListener('mousemove', this.onPanMove);
        document.addEventListener('mouseup', this.stopPanning);
    };

    onPanMove = (e) => {
        if (!this.panning || !this.pdfViewerRef.current) return;

        this.pdfViewerRef.current.scrollLeft = this.panStart.scrollLeft - (e.clientX - this.panStart.x);
        this.pdfViewerRef.current.scrollTop = this.panStart.scrollTop - (e.clientY - this.panStart.y);
    };

    stopPanning = () => {
        if (!this.panning) return;

        this.panning = false;
        this.setState({ isPanning: false });
        document.removeEventListener('mousemove', this.onPanMove);
        document.removeEventListener('mouseup', this.stopPanning);
    };

    renderImage = () => {
        const { file } = this.state;
        return (
            <div className="image-wrapper">
                <img src={file} alt="Document preview" />
            </div>
        );
    };

    renderPDF = () => {
        const { file, pageNumber, numPages, scale, loading, isPanning } = this.state;

        if (loading) {
            return <Spin />;
        }

        return (
            <div className="pdf-container">
                <div
                    className={`pdf-viewer${isPanning ? ' panning' : ''}`}
                    ref={this.pdfViewerRef}
                    onMouseDown={this.onPanStart}
                >
                    <Document
                        file={file}
                        onLoadSuccess={this.onDocumentLoadSuccess}
                    >
                        <Page
                            pageNumber={pageNumber}
                            scale={scale}
                            renderAnnotationLayer={false}
                            renderTextLayer={false}
                        />
                    </Document>
                </div>

                <div className="pdf-controls">
                    <Button.Group>
                        <Button onClick={this.scaleIn}>
                            <Icon type="zoom-in" />
                        </Button>
                        <Button onClick={this.scaleOut}>
                            <Icon type="zoom-out" />
                        </Button>
                    </Button.Group>
                </div>

                {numPages && numPages > 1 && (
                    <Pagination
                        simple
                        onChange={this.changePage}
                        current={pageNumber}
                        total={numPages}
                        pageSize={1}
                        style={{ marginTop: 16, textAlign: 'center' }}
                    />
                )}
            </div>
        );
    };

    renderVideo = () => {
        const { file } = this.state;
        return (
            <div className="video-wrapper">
                <video src={file} controls>
                    <FormattedMessage id="documents.invalidfile" />
                </video>
            </div>
        );
    };

    renderAudio = () => {
        const { file } = this.state;
        return (
            <div className="audio-wrapper">
                <audio src={file} controls>
                    <FormattedMessage id="documents.invalidfile" />
                </audio>
            </div>
        );
    };

    renderHtml = () => {
        const { file } = this.state;
        return (
            <div className="html-wrapper">
                <iframe src={file} sandbox="allow-same-origin" title="html-preview" />
            </div>
        );
    };

    renderUnsupported = () => {
        return (
            <Fragment>
                <p><FormattedMessage id="documents.invalidfile" /></p>
            </Fragment>
        );
    };

    renderContent = () => {
        const { extension } = this.state;
        const { fileBlob } = this.props;
        const fileType = mimeToType(fileBlob && fileBlob.type) || extensionToType(extension);

        switch (fileType) {
            case 'image':
                return this.renderImage();
            case 'pdf':
                return this.renderPDF();
            case 'video':
                return this.renderVideo();
            case 'audio':
                return this.renderAudio();
            case 'html':
                return this.renderHtml();
            default:
                return this.renderUnsupported();
        }
    };

    render() {
        const { file, loading, isFullscreen } = this.state;
        const { file: fileData } = this.props;

        if (loading) {
            return <Spin />;
        }

        if (!fileData) {
            return <div />;
        }

        if (!file) {
            return (
                <div className="file-placeholder">
                    <FormattedMessage id="documents.fetcherror" />
                </div>
            );
        }

        return (
            <div className={`ged-file-viewer${isFullscreen ? ' fullscreen' : ''}`}>
                <div className="fullscreen-toggle">
                    <Button shape="circle" onClick={this.toggleFullscreen}>
                        <Icon type={isFullscreen ? 'fullscreen-exit' : 'fullscreen'} />
                    </Button>
                </div>
                {this.renderContent()}
            </div>
        );
    }
}

GedFileViewer.propTypes = {
    file: PropTypes.shape({
        Id: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
        Desc: PropTypes.string,
        IsDir: PropTypes.bool.isRequired
    }),
    fileBlob: PropTypes.object,
    intl: intlShape.isRequired
};

export default injectIntl(GedFileViewer);
